import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PermissionMatrix from '../components/permissions/PermissionMatrix';
import permissionService from '../services/permissionService';
import queueService from '../services/queueService';

/**
 * «I miei permessi» (spec §10.6): pagina personale in sola lettura.
 *
 * Non ha guardia di rotta e non ne vuole una: chiunque sia autenticato vede i propri
 * permessi, e chi non ne ha nessuno è precisamente chi ha più bisogno di aprirla.
 *
 * L'attribuzione al ruolo di provenienza è la ragione per cui la pagina vale: risponde a
 * «perché posso farlo» e non solo a «posso farlo». È lo strumento di diagnosi di primo
 * livello quando un utente segnala un accesso negato.
 *
 * Dal modello a gruppi (fase F3) la pagina racconta le **concessioni**, non i ruoli: il ruolo
 * dice cosa si può fare, il gruppo su quali dati. Prima mostrava i ruoli da soli e l'unione
 * delle code di tutti i gruppi, dicendo che i permessi ad ambito «Gruppo» valevano su tutte:
 * con Operatore in Fatture e Sola lettura in Bolle faceva credere di poter modificare anche i
 * Codex di Bolle. Qui ogni gruppo mostra i propri ruoli e il proprio contenuto, e la griglia
 * dice per ogni azione da quale ruolo e in quale gruppo arriva.
 *
 * Un punto di §10.6 non è consegnato: «gli strumenti abilitati, ognuno col ruolo di
 * provenienza» richiede il modello degli strumenti di §6.5, che nel backend non esiste. Il
 * mockup lo mostra come scheda in fondo; qui è assente invece che finto, perché una scheda
 * vuota o con dati inventati direbbe che la funzionalità c'è.
 */
export default function MyPermissions() {
    const [dati, setDati] = useState(null);
    const [errore, setErrore] = useState(false);
    const [catalogoCode, setCatalogoCode] = useState([]);

    useEffect(() => {
        let vivo = true;
        permissionService.getMyPermissionsGrid()
            .then((risposta) => { if (vivo) setDati(risposta); })
            .catch(() => { if (vivo) setErrore(true); });

        // Il catalogo porta solo nomi e descrizioni delle code, e la sua assenza non e' un
        // errore di questa pagina: chi non ha `queues.read` riceve 403 pur potendo avere code
        // nei suoi gruppi. Si fallisce in silenzio e si mostrano i soli candidate group --
        // che e' comunque la risposta alla domanda «quali sono». Null, non lista vuota: un
        // catalogo illeggibile non dice che quelle code non esistono.
        queueService.getAll()
            .then((elenco) => { if (vivo) setCatalogoCode(elenco || []); })
            .catch(() => { if (vivo) setCatalogoCode(null); });

        return () => { vivo = false; };
    }, []);

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                {/* «Profilo» non è un collegamento: una pagina di profilo non esiste, e un
                    breadcrumb che non porta da nessuna parte è peggio di uno statico. */}
                <Typography color="text.secondary">Profilo</Typography>
                <Typography color="text.primary">I miei permessi</Typography>
            </Breadcrumbs>

            <Typography variant="h4" sx={{ mb: 1 }}>I miei permessi</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
                Che cosa puoi fare in Automate e su quali dati. Ogni ruolo ti è concesso in un
                gruppo: il ruolo dice che cosa puoi fare, il gruppo su quali code e tipi di
                Codex. Per cambiarlo serve un amministratore.
            </Typography>

            {errore && (
                <Alert severity="error">
                    Non è stato possibile caricare i tuoi permessi. Riprova fra poco; se il
                    problema resta, segnalalo a chi amministra l'ambiente.
                </Alert>
            )}

            {!errore && !dati && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!errore && dati && (
                <>
                    <SchedaConcessioni
                        concessioni={dati.grants || []}
                        catalogo={catalogoCode}
                    />

                    {dati.resourceGroups.map((gruppo) => (
                        <Accordion
                            key={gruppo.label}
                            disableGutters
                            // Senza questo la tabella di ogni gruppo si costruisce anche da
                            // chiusa: diciassette risorse rese a ogni apertura di pagina per
                            // mostrarne quelle di una sezione sola.
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                            elevation={0}
                            variant="outlined"
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ flexGrow: 1 }}>{gruppo.label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                    {contaRisorse(gruppo)}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, borderTop: 1, borderColor: 'divider' }}>
                                <PermissionMatrix resourceGroup={gruppo} />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </>
            )}
        </Box>
    );
}

/**
 * «3 risorse su 6», «1 risorsa su 4», «nessuna risorsa su 3».
 *
 * Lo zero si scrive a parole perché è l'informazione che conta di più in questa pagina — un
 * intero gruppo a cui non si accede — e «0 risorse su 3» si legge come un conteggio qualsiasi.
 */
const contaRisorse = ({ accessible, total }) => {
    if (accessible === 0) return `nessuna risorsa su ${total}`;
    if (accessible === 1) return `1 risorsa su ${total}`;
    return `${accessible} risorse su ${total}`;
};

/**
 * Le concessioni raggruppate per gruppo, «ogni gruppo» in testa: l'ordine in cui arrivano dal
 * backend, che resta tale. Più ruoli nello stesso gruppo condividono lo stesso contenuto, e
 * ripeterlo per ogni ruolo farebbe credere a due insiemi di code diversi.
 */
function perGruppo(concessioni) {
    const gruppi = new Map();
    concessioni.forEach((c) => {
        const chiave = c.groupId ?? '';
        if (!gruppi.has(chiave)) {
            gruppi.set(chiave, {
                groupId: c.groupId,
                groupCode: c.groupCode,
                groupLabel: c.groupLabel,
                queues: c.queues || [],
                codexTypes: c.codexTypes || [],
                ruoli: [],
            });
        }
        gruppi.get(chiave).ruoli.push(c);
    });
    return [...gruppi.values()];
}

/**
 * «Le tue concessioni»: in quale gruppo ti è concesso ciascun ruolo, e che cosa contiene quel
 * gruppo.
 *
 * È la risposta a «perché non vedo quel Codex»: un permesso ad ambito «Gruppo» vale sulle code
 * e sui tipi di Codex del gruppo della concessione che lo dà, non su quelli degli altri. Per
 * questo il contenuto sta sotto il suo gruppo e non in un elenco unico.
 */
function SchedaConcessioni({ concessioni, catalogo }) {
    const codaPerCandidateGroup = new Map();
    (catalogo || []).forEach((coda) => {
        if (coda.candidateGroup) codaPerCandidateGroup.set(coda.candidateGroup, coda);
    });
    const gruppi = perGruppo(concessioni);

    return (
        <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{
                px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider',
                display: 'flex', alignItems: 'center',
            }}>
                <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Le tue concessioni
                </Typography>
                {gruppi.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {contaConcessioni(concessioni.length)}
                    </Typography>
                )}
            </Box>

            {gruppi.length === 0 ? (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info">
                        Nessun ruolo assegnato. Senza un ruolo in un gruppo non hai alcun
                        permesso: chiedi a chi amministra l'ambiente di assegnartene uno.
                    </Alert>
                </Box>
            ) : (
                <>
                    {/* Una riga per gruppo: chi la legge cerca «dove», e il gruppo è la chiave.
                        Le descrizioni stanno nel tooltip per tenere la riga su una linea. */}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Gruppo</TableCell>
                                    <TableCell>Ruoli</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Tipi di Codex</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {gruppi.map((gruppo) => (
                                    <RigaGruppo
                                        key={gruppo.groupId ?? 'ogni-gruppo'}
                                        gruppo={gruppo}
                                        codaPerCandidateGroup={codaPerCandidateGroup}
                                        catalogoLeggibile={catalogo != null}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                            I permessi ad ambito «Gruppo» valgono sulle code e sui tipi di Codex
                            del gruppo in cui ti è concesso il ruolo che li dà. Quelli ad ambito
                            «Organizzazione» valgono su tutto, in qualunque gruppo ti sia
                            concesso il ruolo.
                        </Typography>
                    </Box>
                </>
            )}
        </Paper>
    );
}

SchedaConcessioni.propTypes = {
    concessioni: PropTypes.arrayOf(PropTypes.shape({
        roleId: PropTypes.string.isRequired,
        roleLabel: PropTypes.string,
        roleDescription: PropTypes.string,
        groupId: PropTypes.string,
        groupCode: PropTypes.string,
        groupLabel: PropTypes.string,
        queues: PropTypes.arrayOf(PropTypes.string),
        codexTypes: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            code: PropTypes.string,
            description: PropTypes.string,
        })),
    })).isRequired,
    // Null quando il catalogo non si e' potuto leggere (manca `queues.read`)
    catalogo: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string,
        description: PropTypes.string,
        candidateGroup: PropTypes.string,
    })),
};

/** «1 concessione», «3 concessioni». Lo zero non passa di qui: ha il suo avviso. */
const contaConcessioni = (quante) => (quante === 1 ? '1 concessione' : `${quante} concessioni`);

/** Una pillola con la descrizione nel tooltip, quando c'è. */
function Pillola({ label, descrizione, ...props }) {
    const chip = <Chip size="small" label={label} {...props} />;
    return descrizione ? <Tooltip title={descrizione}><span>{chip}</span></Tooltip> : chip;
}

Pillola.propTypes = {
    label: PropTypes.string.isRequired,
    descrizione: PropTypes.string,
};

const cellaPillole = { display: 'flex', gap: 0.5, flexWrap: 'wrap' };

/**
 * Il tooltip di una coda senza nome né descrizione. Senza `queues.read` il catalogo risponde
 * 403: la coda esiste, è chi guarda a non poterne leggere i dettagli, e dire «non presente»
 * farebbe pensare a una coda cancellata.
 */
const descrizioneSenzaCatalogo = (catalogoLeggibile) => (catalogoLeggibile
    ? 'Non presente nel catalogo delle code'
    : 'Nome e descrizione della coda richiedono il permesso di vedere le code');

function RigaGruppo({ gruppo, codaPerCandidateGroup, catalogoLeggibile }) {
    const ogniGruppo = gruppo.groupId == null;
    const vuoto = gruppo.queues.length === 0 && gruppo.codexTypes.length === 0;

    // Ordinate sul nome mostrato, non sul candidate group: è quello che si legge.
    const code = gruppo.queues
        .map((cg) => ({ cg, coda: codaPerCandidateGroup.get(cg) }))
        .sort((a, b) => (a.coda?.code || a.cg).localeCompare(b.coda?.code || b.cg, 'it'));

    return (
        <TableRow
            data-testid={`concessioni-${gruppo.groupId ?? 'ogni-gruppo'}`}
            sx={{ verticalAlign: 'top' }}
        >
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2" fontWeight={500}>
                    {ogniGruppo ? 'Ogni gruppo' : gruppo.groupLabel}
                </Typography>
                {!ogniGruppo && gruppo.groupCode && (
                    <Typography variant="caption" color="text.secondary">
                        {gruppo.groupCode}
                    </Typography>
                )}
            </TableCell>

            <TableCell>
                <Box sx={cellaPillole}>
                    {gruppo.ruoli.map((ruolo) => (
                        <Pillola
                            key={ruolo.roleId}
                            label={ruolo.roleLabel}
                            descrizione={ruolo.roleDescription}
                            variant="outlined"
                            color="primary"
                        />
                    ))}
                </Box>
            </TableCell>

            {ogniGruppo && (
                <TableCell colSpan={2}>
                    <Typography variant="body2" color="text.secondary">
                        Tutte le code e tutti i tipi di Codex, presenti e futuri
                    </Typography>
                </TableCell>
            )}

            {!ogniGruppo && vuoto && (
                <TableCell colSpan={2}>
                    <Typography variant="body2" color="warning.main">
                        Nessuna coda né tipo di Codex: qui i permessi ad ambito «Gruppo» non ti
                        fanno vedere nessun dato
                    </Typography>
                </TableCell>
            )}

            {!ogniGruppo && !vuoto && (
                <>
                    <TableCell>
                        <Box sx={cellaPillole}>
                            {code.length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
                            {code.map(({ cg, coda }) => (
                                // Una coda che il catalogo non conosce resta visibile col solo
                                // candidate group, e il perché si dichiara: sul seed di serie è il
                                // caso di `validazione_fatture_utenza`. Se il catalogo non si è
                                // potuto leggere il perché è un altro, e va detto quello.
                                <Pillola
                                    key={cg}
                                    label={coda?.code || cg}
                                    descrizione={coda ? coda.description : descrizioneSenzaCatalogo(catalogoLeggibile)}
                                    variant={coda ? 'filled' : 'outlined'}
                                />
                            ))}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box sx={cellaPillole}>
                            {gruppo.codexTypes.length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
                            {gruppo.codexTypes.map((tipo) => (
                                <Pillola key={tipo.id} label={tipo.code} descrizione={tipo.description} />
                            ))}
                        </Box>
                    </TableCell>
                </>
            )}
        </TableRow>
    );
}

RigaGruppo.propTypes = {
    gruppo: PropTypes.shape({
        groupId: PropTypes.string,
        groupCode: PropTypes.string,
        groupLabel: PropTypes.string,
        queues: PropTypes.arrayOf(PropTypes.string).isRequired,
        codexTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
        ruoli: PropTypes.arrayOf(PropTypes.object).isRequired,
    }).isRequired,
    codaPerCandidateGroup: PropTypes.instanceOf(Map).isRequired,
    catalogoLeggibile: PropTypes.bool.isRequired,
};
